import '@vp/digital-faq-lib-ubik/dist/index.css'
import styles, { stylesheet } from './wixDomainsPdp.scss'
import React, { useCallback, useEffect, useState } from 'react'
import {
  Banner,
  BannerDescription,
  BannerEyebrow,
  BannerImage,
  BannerImageContainer,
  BannerText,
  BannerTextContainer,
  BannerTitle,
  Button,
  FluidImage,
  P,
  ResponsiveImage,
  Span,
  Typography,
  useScreenClass,
} from '@vp/swan'
import { isProduction } from '@vp/digital-environment-lib-ubik'
import { trackWixConnectDomainsCTAClick, trackWixFindMyDomainCTAClick, trackWixGetStartedCTAClick, trackGwsPromotionViewed } from './WixDomainsPDPAnalytics'
import {
  SearchComponent,
  SearchComponentCTAModel,
  SearchComponentInputModel,
  useHasOwnDomainValue,
  useSearchedDomainValue,
} from '@vp/digital-search-component-lib-ubik'

import { CANONICAL_ID } from '@vp/digital-auth-lib-ubik'
import { getPartnerAccountProducts, PartnerAccountProducts } from '@vp/digital-paid-lib-ubik'
import { isFeatureEnabled, initialize, whenAvailable } from '@vp/ab-reader'

import FaqSection from './components/FaqSection'
import QuestionsSection from './components/QuestionsSection'
import { urlParamsEnum } from '../../common/enum/urlParamsEnum'
import { sourceEnum } from '../../common/enum/sourceEnum'
import { getWixPartnerConnectDomainBuildWebsiteUrl } from '../../common/helpers/wixDomainsUrls'
import Redirect from '../../common/Redirect'
import { getResponsiveCloudinaryImage, BREAKPOINTS_SIZES } from '../../common/helpers/cloudinaryImageResizerHelper'
import { useLogger, useStyles } from '@vp/ubik-context'
import { VPAuthIdentity } from '../../common/types/Types'
import { useSignedInAction } from '../../common/helpers/useSignedInAction'
import GwsPromo from './components/GwsPromoBanner'
import { Hero } from '../../../types/Hero'
import { CommonFeatures } from '../../../types/CommonFeatures'
import { LocalizedUrl } from '../../../types/LocalizedUrl'
import DoubleBannerSection from './components/DoubleBannerSection'
export interface WixDomainsPdpProps {
  locale: string
  heroContent: Hero
  commonLocalizedUrls: { [key in string]: LocalizedUrl }
  urlWixPartnerGetStartedTnCAcceptedlUrlsPdp: any
  domainNameImportance: CommonFeatures
  whyThroughVista: CommonFeatures
  whyFeatures: CommonFeatures
  gwsPromoBanner: CommonFeatures
  createWebsiteSubHero: Hero
  customersChooseWixSubHero: Hero
  faqs: CommonFeatures
}
const WixDomainsPdp = (pageContext: WixDomainsPdpProps) => {
  useStyles(stylesheet)
  const {
    urlWixPartnerGetStartedTnCAcceptedlUrlsPdp,
    locale,
    commonLocalizedUrls: { googleWorkspaceUrlsLocalized, wixDomainsSearchAndSuggestionsUrlLocalized, wixWebsitesUrlLocalized },
    heroContent,
    domainNameImportance,
    whyThroughVista,
    whyFeatures,
    gwsPromoBanner,
    customersChooseWixSubHero,
    createWebsiteSubHero,
    faqs,
  } = pageContext
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [redirectPath, setRedirectPath] = useState<string | null>(null)
  const [isGetStartedLoading, setIsGetStartedLoading] = useState(false)
  const [domain, setDomain, resetDomain] = useSearchedDomainValue()
  const [, setHasOwnDomainValue, resetHasOwnDomainValue] = useHasOwnDomainValue()
  const [connectDomainsLinkUrl, setConnectDomainsLinkUrl] = useState<string | null>(null)
  const logger = useLogger()
  const [isLocalFaqsDisplayed, setIsLocalFaqsDisplayed] = useState(true)

  useEffect(() => {
    initialize()
    whenAvailable((available) => {
      if (!available) {
        setIsLocalFaqsDisplayed(true)
      }
      const enabled = isFeatureEnabled('digital_vxw_faqs')
      if (enabled) {
        setIsLocalFaqsDisplayed(false)
      } else {
        setIsLocalFaqsDisplayed(true)
      }
    }, 1000)
  }, [])

  useEffect(() => {
    trackGwsPromotionViewed()
  }, [locale])

  useEffect(() => {
    const clearRedirection = () => {
      setIsRedirecting(false)
      setIsGetStartedLoading(false)
      setRedirectPath(null)
    }

    window.addEventListener('pageshow', clearRedirection)

    return () => {
      window.removeEventListener('pageshow', clearRedirection)
    }
  }, [])

  let querySiteId: string | null = null
  if (typeof window !== 'undefined' && window.location?.search) {
    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    querySiteId = urlParams?.get(urlParamsEnum.SITE_ID)?.toLowerCase() ?? urlParams?.get(urlParamsEnum.UPGRADE_SITE_ID)?.toLowerCase() ?? null
  }

  const source = sourceEnum.DOMAINS_PDP

  const urlWixPartnerGetStartedTnCAcceptedlUrl = isProduction()
    ? urlWixPartnerGetStartedTnCAcceptedlUrlsPdp.prod
    : urlWixPartnerGetStartedTnCAcceptedlUrlsPdp.qa

  const handleSearchCTA = useCallback(
    (domain: any) => {
      setDomain(domain)
      handleWixFindMyDomainNavigation(false)
    },
    [handleWixFindMyDomainNavigation],
  )

  const ctaProps: SearchComponentCTAModel = {
    onClickHandler: handleSearchCTA,
  }

  const inputProps: SearchComponentInputModel = {
    textInputPlaceholder: heroContent.actions?.[1].description ?? '',
    ariaLabel: heroContent?.description ?? '',
    value: domain,
    setValue: setDomain,
  }

  const fetchSiteList = async (locale: string, identity: VPAuthIdentity) => {
    const data = await getPartnerAccountProducts(identity.profile[CANONICAL_ID] as string)
    const redirectUrl = getConnectDomainUrl(data)
    setConnectDomainsLinkUrl(redirectUrl)
    trackWixConnectDomainsCTAClick(redirectUrl)
  }

  const [, , initiateFetchSiteList, isFetchingSiteList] = useSignedInAction<string, void>(fetchSiteList, 'fetch-site-list-after-sign-in')

  function getConnectDomainUrl(productDetails: PartnerAccountProducts): string {
    const numberOfSites = productDetails?.wixSites?.length
    let siteId: string | undefined
    if (numberOfSites === 1) {
      siteId = productDetails?.wixSites?.[0].id
    }
    return getWixPartnerConnectDomainBuildWebsiteUrl(locale, source, domain ?? '', source, siteId)
  }

  const isMobile = useScreenClass() === 'xs'

  async function handleGetStartedNavigation() {
    logger.debug('Clicking Domains PDP Get Started to Wix IntroFunnel Navigate')
    let url = urlWixPartnerGetStartedTnCAcceptedlUrl
    if (querySiteId) {
      url += `?${urlParamsEnum.SITE_ID}=${querySiteId}`
    }
    if (isMobile) {
      url += `&${urlParamsEnum.DEVICE_TYPE}=mobile`
    }
    trackWixGetStartedCTAClick(url)
    setIsRedirecting(true)
    setIsGetStartedLoading(true)
    setRedirectPath(url)
  }

  function handleWixConnectDomainsNavigation() {
    logger.debug('Clicking Domains PDP Connect Domains link')
    setHasOwnDomainValue(true)
    initiateFetchSiteList(locale)
  }

  function handleWixFindMyDomainNavigation(navigateWithouData = false) {
    if (navigateWithouData) {
      resetDomain()
      resetHasOwnDomainValue()
    }
    setHasOwnDomainValue(false)
    logger.debug('Clicking Wix Domains PDP Find My domain link')
    let url = wixDomainsSearchAndSuggestionsUrlLocalized.url
    url += `?${urlParamsEnum.REQUESTOR}=` + sourceEnum.DOMAINS_PDP
    if (querySiteId) {
      url += `&${urlParamsEnum.UPGRADE_SITE_ID}=${querySiteId}`
    }
    trackWixFindMyDomainCTAClick(url)
    setIsRedirecting(true)
    setRedirectPath(url)
  }

  const CONNECT_DOMAINS_KEY = 'wixConnectDomainsLink'
  const connectDomainsLink = heroContent.heroMetadata?.find((metadata: CommonFeatures) => metadata.key === CONNECT_DOMAINS_KEY)

  return (
    <Redirect redirectPath={redirectPath || connectDomainsLinkUrl} isRedirecting={isRedirecting || isFetchingSiteList}>
      <>
        {/* Wix Domains PDP hero */}
        {heroContent && (
          <Banner bgc="info" darkMode id="digital-search-component-section">
            <BannerTextContainer>
              <BannerText pb={0}>
                <BannerEyebrow>
                  <ResponsiveImage style={{ maxWidth: '11em' }} src={heroContent.media?.[0].mediaLink?.[0].secure_url} alt="" />
                </BannerEyebrow>
                <BannerTitle as="h1" fontSkin="title-display">
                  {heroContent.title}
                </BannerTitle>
                <BannerDescription className={styles.wixDomainsPdpHeroDescription}>
                  <Typography mt="6" mb="6" component="p">
                    {heroContent.description}
                  </Typography>
                  <SearchComponent ctaProps={ctaProps} inputProps={inputProps} className="wix-domains-pdp__hero-search" />
                  {connectDomainsLink && (
                    <P mb={{ xs: 0, sm: 5 }}>
                      <Span paddingRight="3">{connectDomainsLink.title}</Span>
                      <Button
                        skin="link"
                        darkMode
                        style={{ textDecoration: 'underline' }}
                        onClick={() => {
                          handleWixConnectDomainsNavigation()
                        }}
                      >
                        {connectDomainsLink.description}
                      </Button>
                    </P>
                  )}
                </BannerDescription>
              </BannerText>
            </BannerTextContainer>
            <BannerImageContainer>
              <BannerImage imageFocalPoint="center center">
                {typeof heroContent.media?.[1].mediaLink?.[0].secure_url === 'string' && (
                  <FluidImage
                    srcSet={getResponsiveCloudinaryImage(heroContent.media?.[1].mediaLink?.[0].secure_url)}
                    sizes={BREAKPOINTS_SIZES}
                    alt=""
                    style={{ objectFit: 'contain' }}
                  />
                )}
              </BannerImage>
            </BannerImageContainer>
          </Banner>
        )}

        <QuestionsSection
          domainNameImportance={domainNameImportance}
          whyThroughVista={whyThroughVista}
          whyFeatures={whyFeatures}
          commonLocalizedUrls={pageContext.commonLocalizedUrls}
        />
        {gwsPromoBanner && <GwsPromo content={gwsPromoBanner} url={googleWorkspaceUrlsLocalized.url} />}

        {/* Wix PDP Create your own Site sub hero AND Wix Domains PDP Customers Choose Wix subhero */}
        <DoubleBannerSection
          handleGetStartedNavigation={handleGetStartedNavigation}
          isGetStartedLoading={isGetStartedLoading}
          handleWixFindMyDomainNavigation={handleWixFindMyDomainNavigation}
          createWebsiteSubHero={createWebsiteSubHero}
          customersChooseWixSubHero={customersChooseWixSubHero}
        />

        {isLocalFaqsDisplayed && <FaqSection wixWebsitesAnonymousPlansUrlLocalized={wixWebsitesUrlLocalized} faqs={faqs} locale={locale} />}
      </>
    </Redirect>
  )
}

export default React.memo(WixDomainsPdp)
